import { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  ArrowLeft,
  UploadSimple,
  FileCsv,
  EnvelopeSimple,
  Phone,
  CheckCircle,
  WarningCircle,
  X,
  MagnifyingGlass,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Header sniffer mirrored from backend so the user gets instant feedback
// without having to upload first. Keep these in sync with _CSV_HEADER_ALIASES.
const HEADER_ALIASES = {
  email: ["email", "emailaddress", "emails", "primaryemail", "workemail", "contactemail", "e_mail", "mail"],
  phone: ["phone", "phonenumber", "phonenumbers", "telephone", "tel", "mobile", "mobilenumber", "contactnumber", "cell", "primaryphone", "workphone"],
  business_name: ["business", "businessname", "company", "companyname", "name", "organization", "org", "businesstitle", "title", "account", "accountname"],
  contact_name: ["contactname", "fullname", "ownername", "firstname", "person", "personname", "leadname", "owner"],
  website: ["website", "url", "domain", "site", "web", "websiteurl"],
  address: ["address", "streetaddress", "street", "fulladdress", "addressline1", "addr", "location"],
  city: ["city", "town", "locality"],
  state: ["state", "region", "stateprovince", "province", "stateregion"],
  zip: ["zip", "zipcode", "postalcode", "postal", "postcode"],
  country: ["country", "countrycode"],
  category: ["category", "industry", "niche", "type", "businesstype", "vertical"],
};
const HEADER_LABELS = {
  email: "Email",
  phone: "Phone",
  business_name: "Business Name",
  contact_name: "Contact Name",
  website: "Website",
  address: "Address",
  city: "City",
  state: "State",
  zip: "Zip",
  country: "Country",
  category: "Category",
};
const normalize = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
const mapColumns = (headers) => {
  const mapping = {};
  for (const col of headers) {
    const key = normalize(col);
    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      if (aliases.includes(key) && !mapping[field]) {
        mapping[field] = col;
      }
    }
  }
  return mapping;
};

const UploadListPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [parseError, setParseError] = useState("");
  const [jobName, setJobName] = useState("");
  const [enrichEmails, setEnrichEmails] = useState(true);
  const [enrichPhones, setEnrichPhones] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const columnMap = useMemo(() => mapColumns(headers), [headers]);
  const hasEmailOrPhone = !!(columnMap.email || columnMap.phone);

  const handleFile = (f) => {
    if (!f) return;
    const name = (f.name || "").toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xlsx") && !name.endsWith(".xls")) {
      toast.error("Please upload a .csv or .xlsx file");
      return;
    }
    setFile(f);
    setParseError("");
    setHeaders([]);
    setRowCount(0);
    if (!jobName) {
      // Default to filename (without extension) so the user can edit it
      setJobName(f.name.replace(/\.(csv|xlsx|xls)$/i, ""));
    }
    if (name.endsWith(".csv")) {
      // Quick client-side header sniff (just the first chunk)
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = String(e.target.result || "");
        const firstLine = text.split(/\r?\n/)[0] || "";
        // Naive split — good enough for the preview. Backend re-parses with pandas.
        const cols = firstLine.split(",").map((c) => c.replace(/^"|"$/g, "").trim()).filter(Boolean);
        setHeaders(cols);
        const lineCount = text.split(/\r?\n/).filter((l) => l.trim()).length;
        setRowCount(Math.max(0, lineCount - 1));
      };
      reader.onerror = () => setParseError("Could not read file");
      reader.readAsText(f.slice(0, 64 * 1024)); // sample first 64KB
    } else {
      // For xlsx, skip client preview — backend will validate
      setHeaders([]);
      setRowCount(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file to upload"); return; }
    if (!jobName.trim()) { toast.error("Enter a job name"); return; }
    if (!enrichEmails && !enrichPhones) { toast.error("Pick at least one enrichment mode"); return; }

    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("job_name", jobName.trim());
      form.append("enrich_emails", String(enrichEmails));
      form.append("enrich_phones", String(enrichPhones));
      const res = await axios.post(`${API}/jobs/upload-csv`, form, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 min for big lists
      });
      toast.success(`Job created with ${res.data.raw_leads_count} leads — processing started`);
      navigate(`/jobs/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4" data-testid="upload-list-page">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6 text-sm"
        data-testid="back-btn"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Upload Your List</h1>
        <p className="text-slate-500 mt-2 text-sm">
          Already have leads from another source? Drop the file here, pick an enrichment mode,
          and we&apos;ll clean, normalize, verify, and push them through the same pipeline as a scraped job.
        </p>
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
          <span>Or</span>
          <button
            onClick={() => navigate("/jobs/new")}
            className="text-[#006994] hover:underline inline-flex items-center gap-1"
            data-testid="goto-scrape-btn"
          >
            <MagnifyingGlass size={12} /> Scrape from LeadFlow Scraper instead
          </button>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900">1. Upload File</CardTitle>
            <CardDescription>
              CSV or Excel. Any column layout — we auto-detect email, phone, business name, city, etc.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              onDrop={onDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => fileInputRef.current?.click()}
              className={`relative cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                dragOver
                  ? "border-[#006994] bg-[#006994]/5"
                  : file
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : "border-slate-200 hover:border-zinc-500 bg-white/50"
              }`}
              data-testid="file-drop-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="file-input"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileCsv size={32} className="text-emerald-400" weight="bold" />
                  <div className="text-left">
                    <p className="text-slate-900 font-medium">{file.name}</p>
                    <p className="text-xs text-slate-500">
                      {(file.size / 1024).toFixed(1)} KB
                      {rowCount > 0 && ` · ~${rowCount.toLocaleString()} rows`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setHeaders([]);
                      setRowCount(0);
                    }}
                    className="ml-2 text-slate-500 hover:text-red-400"
                    data-testid="clear-file-btn"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <UploadSimple size={32} className="text-slate-500" />
                  <p className="text-slate-700 font-medium">Drop your CSV/XLSX file here</p>
                  <p className="text-xs text-slate-500">or click to browse · up to 200,000 rows</p>
                </div>
              )}
            </div>

            {parseError && (
              <div className="text-sm text-red-400 flex items-center gap-2">
                <WarningCircle size={16} /> {parseError}
              </div>
            )}

            {/* Column mapping preview */}
            {file && headers.length > 0 && (
              <div className="rounded-lg bg-white/50 border border-slate-200 p-4" data-testid="column-mapping">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-medium">
                  Detected columns ({Object.keys(columnMap).length} mapped / {headers.length} total)
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {Object.entries(HEADER_LABELS).map(([field, label]) => {
                    const matched = columnMap[field];
                    return (
                      <div key={field} className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">{label}</span>
                        {matched ? (
                          <span className="inline-flex items-center gap-1 text-emerald-400 font-mono text-xs">
                            <CheckCircle size={12} weight="fill" /> {matched}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">— not found</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!hasEmailOrPhone && (
                  <div className="mt-3 text-xs text-amber-400 flex items-start gap-2">
                    <WarningCircle size={14} className="mt-0.5 flex-shrink-0" />
                    <span>No email or phone column detected. The upload will fail — your file needs at least one column named like &ldquo;email&rdquo;, &ldquo;phone&rdquo;, or &ldquo;mobile&rdquo;.</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900">2. Job Name</CardTitle>
            <CardDescription>What should we call this batch in the dashboard?</CardDescription>
          </CardHeader>
          <CardContent>
            <Label htmlFor="job-name" className="sr-only">Job name</Label>
            <Input
              id="job-name"
              value={jobName}
              onChange={(e) => setJobName(e.target.value)}
              placeholder="e.g. Q2 Long Beach Restaurants - cleaned"
              className="bg-white/50 border-slate-200 focus:border-[#006994] focus:ring-1 focus:ring-[#006994] text-slate-900"
              maxLength={200}
              data-testid="job-name-input"
            />
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900">3. Enrichment Mode</CardTitle>
            <CardDescription>
              Uploaded lists skip the LeadFlow Scraper email lookup — we use ONLY the data you provide.
              Email mode dedupes by email + verifies every email via MillionVerifier.
              Phone mode normalizes phones to E.164 (ready for voice drops / SMS) and dedupes by phone.
              Pick both to keep leads that have either channel.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer p-4 rounded-lg bg-[#0EA5E9]/5 border border-[#0EA5E9]/20">
              <input
                type="checkbox"
                checked={enrichEmails}
                onChange={(e) => setEnrichEmails(e.target.checked)}
                className="w-5 h-5 rounded border-[#0EA5E9]/50 bg-slate-100 text-[#0EA5E9] focus:ring-[#0EA5E9] focus:ring-offset-0 accent-[#0EA5E9]"
                data-testid="enrich-emails-checkbox"
              />
              <div>
                <div className="flex items-center gap-2">
                  <EnvelopeSimple size={18} className="text-[#0EA5E9]" />
                  <span className="text-slate-900 font-medium">Email Mode</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Dedupe by email, verify every email with MillionVerifier, then push to Instantly. No external email lookup — we use the emails already in your CSV.
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer p-4 rounded-lg bg-[#006994]/5 border border-[#006994]/20">
              <input
                type="checkbox"
                checked={enrichPhones}
                onChange={(e) => setEnrichPhones(e.target.checked)}
                className="w-5 h-5 rounded border-[#006994]/50 bg-slate-100 text-[#006994] focus:ring-[#006994] focus:ring-offset-0 accent-[#006994]"
                data-testid="enrich-phones-checkbox"
              />
              <div>
                <div className="flex items-center gap-2">
                  <Phone size={18} className="text-[#006994]" />
                  <span className="text-slate-900 font-medium">Phone Enrichment (Cold Call / Voice Drop)</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Normalize phone numbers to E.164, dedupe, and push to a GoHighLevel voice-drop workflow.
                </p>
              </div>
            </label>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="border-slate-200 text-slate-700 hover:bg-slate-100"
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!file || !jobName.trim() || uploading}
            className="bg-[#006994] text-white hover:bg-[#006994]/90 disabled:opacity-40"
            data-testid="submit-upload-btn"
          >
            {uploading ? "Uploading…" : "Upload & Start Processing"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UploadListPage;
